import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Mail, Facebook, Instagram, Music2, Globe, Menu, X, ChevronDown } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import DirectBookingWidget from './DirectBookingWidget';
import { sectionPath, homePath, switchLangPath } from '../siteRoutes';

const Header = () => {
  const { hotelInfo } = useHotel();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang, t, languages } = useLanguage();

  // Any of "/", "/en", "/en/", "/de/", etc. counts as the homepage — the locale-prefixed
  // paths all render the same <HomePage /> component. Without this, clicking a nav
  // link on /en/ would hard-reload to /#section and lose the hash after locale redirect.
  const SUPPORTED = ['en', 'me', 'sq', 'de', 'ru', 'pl', 'hu'];
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isHomePage = pathSegments.length === 0 || (pathSegments.length === 1 && SUPPORTED.includes(pathSegments[0]));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    if (!isHomePage) {
      // We're on a non-home page (e.g., /en/room/5) — navigate to the correct
      // locale home with a hash so the scroll happens on load.
      const locale = SUPPORTED.includes(pathSegments[0]) ? pathSegments[0] : '';
      const homeUrl = locale ? `/${locale}/#${id}` : `/#${id}`;
      window.location.href = homeUrl;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const currentLang = languages.find(l => l.code === lang) || languages[0];
  // Language = URL: switching moves to the same page under /{lang}/ (English has no prefix).
  const switchLanguage = (code) => {
    const target = switchLangPath(location.pathname, code);
    if (target !== location.pathname) navigate(`${target}${location.search}${location.hash}`, { replace: true, state: { lang: code } });
    else setLang(code);
  };

  const navItems = [
    { id: 'home', label: t('nav.home'), to: homePath(location.pathname) },
    { id: 'rooms', label: t('nav.rooms'), to: sectionPath('rooms', location.pathname) },
    { id: 'amenities', label: t('nav.amenities'), to: sectionPath('amenities', location.pathname) },
    { id: 'gallery', label: t('nav.gallery'), to: sectionPath('gallery', location.pathname) },
    { id: '360-tour', label: t('nav.virtualTour'), to: sectionPath('virtual-tour', location.pathname) },
    { id: 'location', label: t('nav.location'), to: sectionPath('location', location.pathname) },
    { id: 'ulcinj-guide', label: t('guide.nav'), to: sectionPath('ulcinj-guide', location.pathname) },
    { id: 'contact', label: t('nav.contact'), to: sectionPath('contact', location.pathname) }
  ];
  const activePath = location.pathname.replace(/\/$/, '') || '/';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Top bar */}
      <div className="bg-[#1a1f2e] text-white py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${hotelInfo.phone}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">{hotelInfo.phone}</span>
            </a>
            <a href={`mailto:${hotelInfo.email}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Mail size={14} />
              <span className="hidden md:inline">{hotelInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <a href={hotelInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
              <Facebook size={16} />
            </a>
            <a href={hotelInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
              <Instagram size={16} />
            </a>
            <a href={hotelInfo.social.tiktok} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
              <Music2 size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="La Rosa Negra" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-xl font-bold text-gray-900">Hotel La Rosa Negra</div>
              <div className="flex items-center gap-1 text-orange-500 text-sm">
                {[...Array(hotelInfo.stars)].map((_, i) => (
                  <span key={i}>★</span>
                ))}
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map(item => (
              <Link 
                key={item.id}
                to={item.to}
                className={`transition-colors font-medium ${activePath === item.to.replace(/\/$/, '') || (item.id === 'home' && activePath === item.to) ? 'text-orange-500' : 'text-gray-700 hover:text-orange-500'}`}
                data-testid={`nav-link-${item.id}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right side - Language, Book Now */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Live coupon pill for returning direct-booking guests */}
            <DirectBookingWidget />
            {/* Language Selector */}
            <div className="relative" ref={langRef}>
              <button 
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Globe size={18} className="text-gray-600" />
                <span className="text-lg">{currentLang.flag}</span>
                <span className="text-sm font-medium text-gray-700">{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showLangDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border py-2 min-w-[180px] z-50">
                  {languages.map(language => (
                    <button
                      key={language.code}
                      onClick={() => { switchLanguage(language.code); setShowLangDropdown(false); }}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-orange-50 transition-colors ${
                        lang === language.code ? 'bg-orange-50 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-xl">{language.flag}</span>
                      <span className="font-medium">{language.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => scrollToSection('booking')} 
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
            >
              {t('nav.bookNow')}
            </button>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle" aria-label="Menu" className="lg:hidden p-2 text-gray-700 hover:text-orange-500 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 flex flex-col gap-2">
            {navItems.map(item => (
              <Link 
                key={item.id}
                to={item.to}
                onClick={() => setIsMobileMenuOpen(false)} 
                className="text-left py-2 text-gray-700 hover:text-orange-500 transition-colors font-medium"
                data-testid={`mobile-nav-link-${item.id}`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="py-3 border-t mt-2">
              <p className="text-sm text-gray-500 mb-2">Language</p>
              <div className="flex flex-wrap gap-2">
                {languages.map(language => (
                  <button
                    key={language.code}
                    onClick={() => switchLanguage(language.code)}
                    className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                      lang === language.code 
                        ? 'bg-orange-100 text-orange-600 border border-orange-200' 
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span>{language.flag}</span>
                    <span>{language.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            <button 
              onClick={() => scrollToSection('booking')} 
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full font-semibold mt-2"
            >
              {t('nav.bookNow')}
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
